import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { SimpleButtonComponent } from '../../../../buttons/simple-medium-button/simple-button.component';
import { SidebarHeaderComponent } from '../../../sidebar-header/sidebar-header.component';
import { SearchInputComponent } from '../../../../search-input/search-input.component';
import { FadedTextComponent } from '../../../../faded-text/faded-text.component';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { ScrolledToBottomDirective } from '@app/directives/scrolled-to-bottom.directive';
import { ChangeActionsStore } from '@app/store/change-actions/change-actions.store';
import { CheckboxComponent } from '@app/framework/inputs/checkbox/checkbox.component';
import { LoadingBarComponent } from '@app/framework/inputs/loading-bar/loading-bar.component';
import { CheckboxChangeEvent } from '@app/framework/interfaces/input.type';
import { PcoLogItem } from '@app/store/pco/pco.types';

@Component({
  selector: 'app-change-action-cor-sidebar',
  imports: [
    FormsModule,
    SimpleButtonComponent,
    SidebarHeaderComponent,
    MoneyPipe,
    SearchInputComponent,
    FadedTextComponent,
    NgClass,
    ScrolledToBottomDirective,
    CheckboxComponent,
    LoadingBarComponent,
  ],
  templateUrl: './change-action-cor-sidebar.component.html',
  styleUrl: './change-action-cor-sidebar.component.scss',
})
export class ChangeActionCORSidebarComponent implements OnInit, OnDestroy {
  protected readonly String = String;
  private readonly changeActionsStore = inject(ChangeActionsStore);

  @Input({ required: true }) name: string;

  @Input({ required: true }) set selectedPCOIds(value: number[]) {
    console.log('Selected PCO IDs updated:', value);
    this.selectedPCOIdsInternal.set(value);
  }

  @Input({ required: true }) isEdit = false;
  @Input({ required: true }) CORId: number;
  @Output() selectedPCOIdsChange = new EventEmitter<number[]>();
  @Output() back = new EventEmitter<void>();
  @Output() saveForm = new EventEmitter<void>();

  searchText$ = new Subject<string>();
  protected selectedPCOIdsInternal = signal<number[]>([]);

  isLoading = this.changeActionsStore.pcosLoading;
  allPCOs = this.changeActionsStore.pcos;
  totalPCOs = computed(() => {
    return this.allPCOs()
      .filter((PCO) => this.selectedPCOIdsInternal().includes(PCO.id))
      .reduce((acc, PCO) => acc + PCO.commitment_dollars, 0);
  });

  isDestroyed$ = new Subject<void>();

  ngOnInit() {
    this.changeActionsStore.loadPCOs();

    this.searchText$.pipe(takeUntil(this.isDestroyed$), debounceTime(300)).subscribe((text) => {
      this.changeActionsStore.pcoSearchTextChanged(text);
    });
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  submitForm() {
    this.selectedPCOIdsChange.emit(this.selectedPCOIdsInternal());
    this.saveForm.emit();
  }

  goBack() {
    this.back.emit();
  }

  onSearch(searchText: string) {
    this.searchText$.next(searchText);
  }

  toggleAllPCOs(checkbox: CheckboxChangeEvent) {
    if (!checkbox.checked) {
      this.selectedPCOIdsInternal.set([]);
      return;
    }

    const allPCOIds = this.allPCOs().map((PCO) => PCO.id);
    this.selectedPCOIdsInternal.set(allPCOIds);
  }

  togglePCO(PCO: PcoLogItem) {
    console.log('Toggling PCO:', PCO);
    const alreadyIncluded = this.selectedPCOIdsInternal().includes(PCO.id);
    this.selectedPCOIdsInternal.update((ids) =>
      alreadyIncluded ? ids.filter((id) => id !== PCO.id) : [...ids, PCO.id],
    );
  }

  scrolledToBottom(event: Event) {
    this.changeActionsStore.loadPCOs(false);
  }
}
